import React, { useId } from "react";
import { Autocomplete, Box, Button, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { FONT, NEUTRAL, ORANGE, RADIUS, VIOLET } from "../../styles/brandTokens";
import type { AutoAgentMonitorFilterOption, AutoAgentMonitorFilterOptions } from "../../types/integration";
import {
  countActiveExecutionFilters,
  datesAreOutOfOrder,
  EXECUTION_FILTER_OPTIONS,
  ExecutionFilters,
  MONITOR_NONE,
  STATUS_CATEGORY_OPTIONS,
} from "../theoAutomationsMonitorFilters";

const FIELD_HEIGHT = 36;

/** Four columns on desktop, two on tablets, one on phones. The date range spans two. */
const GRID_COLUMNS = { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" };

const inputSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    minHeight: FIELD_HEIGHT,
    borderRadius: `${RADIUS.control}px`,
    background: NEUTRAL.surface,
    fontFamily: FONT.sans,
    fontSize: 13.5,
    color: NEUTRAL.text,
    "& fieldset": { borderColor: NEUTRAL.borderStrong },
    "&:hover fieldset": { borderColor: NEUTRAL.textMuted },
    "&.Mui-focused fieldset": { borderColor: VIOLET.main, borderWidth: 1 },
  },
  "& .MuiOutlinedInput-input::placeholder": { color: NEUTRAL.textMuted, opacity: 1 },
  "& .MuiAutocomplete-tag": {
    height: 22,
    maxWidth: 140,
    fontFamily: FONT.sans,
    fontSize: 12,
    borderRadius: `${RADIUS.control}px`,
    backgroundColor: VIOLET.tint,
    color: VIOLET.dark,
  },
};

const labelSx = {
  display: "block",
  mb: 0.5,
  fontFamily: FONT.sans,
  fontSize: 12,
  fontWeight: 600,
  color: NEUTRAL.textSecondary,
};

const FilterField: React.FC<{
  label: string;
  inputId?: string;
  span?: number;
  children: React.ReactNode;
}> = ({ label, inputId, span = 1, children }) => (
  <Box sx={{ minWidth: 0, gridColumn: { xs: "auto", sm: `span ${span}` } }}>
    <Typography component="label" htmlFor={inputId} sx={labelSx}>
      {label}
    </Typography>
    {children}
  </Box>
);

const selectedFilterOptions = (
  options: AutoAgentMonitorFilterOption[],
  keys: string[],
  fallbackLabel: (key: string) => string
): AutoAgentMonitorFilterOption[] =>
  keys.map((key) => options.find((option) => option.key === key) || { key, label: fallbackLabel(key), count: 0 });

const MultiFilter: React.FC<{
  label: string;
  placeholder: string;
  options: AutoAgentMonitorFilterOption[];
  selectedKeys: string[];
  fallbackLabel: (key: string) => string;
  onChange: (keys: string[]) => void;
}> = ({ label, placeholder, options, selectedKeys, fallbackLabel, onChange }) => {
  const inputId = useId();
  return (
    <FilterField label={label} inputId={inputId}>
      <Autocomplete
        multiple
        id={inputId}
        size="small"
        options={options}
        value={selectedFilterOptions(options, selectedKeys, fallbackLabel)}
        onChange={(_, value) => onChange(value.map((option) => option.key))}
        getOptionLabel={(option) => option.label}
        getOptionKey={(option) => option.key}
        isOptionEqualToValue={(left, right) => left.key === right.key}
        limitTags={1}
        disableCloseOnSelect
        renderInput={(params) => (
          <TextField {...params} placeholder={selectedKeys.length ? "" : placeholder} sx={inputSx} />
        )}
        renderOption={({ key, ...props }, option) => (
          <li key={key} {...props}>
            <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ width: "100%" }}>
              <Typography sx={{ fontFamily: FONT.sans, fontSize: 13.5 }} noWrap>
                {option.label}
              </Typography>
              {option.count ? (
                <Typography sx={{ fontFamily: FONT.sans, fontSize: 12, color: NEUTRAL.textMuted }}>{option.count}</Typography>
              ) : null}
            </Stack>
          </li>
        )}
      />
    </FilterField>
  );
};

const DateRange: React.FC<{
  startedAfter: string;
  startedBefore: string;
  onChange: (patch: Partial<ExecutionFilters>) => void;
}> = ({ startedAfter, startedBefore, onChange }) => {
  const afterId = useId();
  const dateSx = { ...inputSx, "& input": { minWidth: 0 } };
  return (
    <FilterField label="Started" inputId={afterId} span={2}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <TextField
          id={afterId}
          size="small"
          type="datetime-local"
          value={startedAfter}
          inputProps={{ "aria-label": "Started after" }}
          onChange={(event) => onChange({ startedAfter: event.target.value })}
          sx={dateSx}
        />
        <ArrowForwardRoundedIcon aria-hidden="true" sx={{ fontSize: 16, color: NEUTRAL.textMuted, flexShrink: 0 }} />
        <TextField
          size="small"
          type="datetime-local"
          value={startedBefore}
          inputProps={{ "aria-label": "Started before" }}
          onChange={(event) => onChange({ startedBefore: event.target.value })}
          sx={dateSx}
        />
      </Stack>
    </FilterField>
  );
};

const FilterNotice: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Stack direction="row" spacing={0.75} alignItems="center" role="status" sx={{ mt: 1.5 }}>
    <ErrorOutlineRoundedIcon aria-hidden="true" sx={{ fontSize: 16, color: ORANGE.text }} />
    <Typography sx={{ fontFamily: FONT.sans, fontSize: 12.5, color: ORANGE.text }}>{children}</Typography>
  </Stack>
);

const ExecutionFilterBar: React.FC<{
  filters: ExecutionFilters;
  options: AutoAgentMonitorFilterOptions | null;
  optionsError: string | null;
  onChange: (patch: Partial<ExecutionFilters>) => void;
  onClear: () => void;
}> = ({ filters, options, optionsError, onChange, onClear }) => {
  const ticketId = useId();
  const activeCount = countActiveExecutionFilters(filters);
  return (
    <Box
      component="section"
      aria-label="Execution filters"
      sx={{ background: NEUTRAL.surface, border: `1px solid ${NEUTRAL.border}`, borderRadius: `${RADIUS.card}px`, p: 2 }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5, minHeight: 28 }}>
        <Typography component="h2" sx={{ fontFamily: FONT.sans, fontSize: 14, fontWeight: 600, color: NEUTRAL.text }}>
          Filters
          {activeCount > 0 ? (
            <Box component="span" sx={{ ml: 1, fontWeight: 400, color: NEUTRAL.textMuted }}>
              {activeCount} active
            </Box>
          ) : null}
        </Typography>
        {activeCount > 0 ? (
          <Button
            size="small"
            onClick={onClear}
            sx={{ fontFamily: FONT.sans, fontSize: 13, textTransform: "none", color: VIOLET.main, minWidth: 0, px: 1 }}
          >
            Clear all
          </Button>
        ) : null}
      </Stack>
      <Box sx={{ display: "grid", gridTemplateColumns: GRID_COLUMNS, columnGap: 1.5, rowGap: 1.5 }}>
        <FilterField label="Ticket" inputId={ticketId}>
          <TextField
            id={ticketId}
            size="small"
            value={filters.ticketQuery}
            placeholder="Number or ID"
            onChange={(event) => onChange({ ticketQuery: event.target.value })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon aria-hidden="true" sx={{ fontSize: 18, color: NEUTRAL.textMuted }} />
                </InputAdornment>
              ),
            }}
            sx={inputSx}
          />
        </FilterField>
        <MultiFilter
          label="Customer"
          placeholder="All customers"
          options={options?.customers || []}
          selectedKeys={filters.customerKeys}
          fallbackLabel={(key) => (key === MONITOR_NONE ? "Unknown customer" : key.replace(/^name:/, "").replace(/^id:/, "Customer "))}
          onChange={(customerKeys) => onChange({ customerKeys })}
        />
        <MultiFilter
          label="Ticket status"
          placeholder="All statuses"
          options={options?.ticket_statuses || []}
          selectedKeys={filters.ticketStatuses}
          fallbackLabel={(key) => (key === MONITOR_NONE ? "Unknown status" : key)}
          onChange={(ticketStatuses) => onChange({ ticketStatuses })}
        />
        <MultiFilter
          label="Status category"
          placeholder="All categories"
          options={STATUS_CATEGORY_OPTIONS.map((option) => ({ ...option, count: 0 }))}
          selectedKeys={filters.statusCategories}
          fallbackLabel={(key) => key}
          onChange={(statusCategories) => onChange({ statusCategories })}
        />
        <MultiFilter
          label="Ticket type"
          placeholder="All types"
          options={options?.ticket_types || []}
          selectedKeys={filters.ticketTypes}
          fallbackLabel={(key) => (key === MONITOR_NONE ? "No ticket type" : key)}
          onChange={(ticketTypes) => onChange({ ticketTypes })}
        />
        <MultiFilter
          label="Execution"
          placeholder="All executions"
          options={EXECUTION_FILTER_OPTIONS.map((option) => ({ ...option, count: 0 }))}
          selectedKeys={filters.executionStatuses}
          fallbackLabel={(key) => key}
          onChange={(executionStatuses) => onChange({ executionStatuses })}
        />
        <DateRange startedAfter={filters.startedAfter} startedBefore={filters.startedBefore} onChange={onChange} />
      </Box>
      {datesAreOutOfOrder(filters) ? (
        <FilterNotice>The start of the range is after its end, so no runs match.</FilterNotice>
      ) : null}
      {optionsError ? (
        <FilterNotice>Customer and status lists didn't load. Ticket and date filters still apply.</FilterNotice>
      ) : null}
    </Box>
  );
};

export default ExecutionFilterBar;
